import { useMemo } from 'react'
import { NumberFormatter, Paper, Skeleton, Table } from '@mantine/core'
import { SoldWoolSummary } from '@shared/components/wool-data/sold-wool-summary/types'
import { IconAlert, AlertLevel } from '@shared/components/icon-alert/icon-alert'
import classes from './sold-wool-summary-table.module.css'

interface SoldWoolSummaryProps {
  data?: Array<SoldWoolSummary>
  isLoading?: boolean
  isError?: boolean
}

export const SoldWoolSummaryTable = ({ data, isLoading, isError }: SoldWoolSummaryProps) => {
  if (isLoading) {
    return <Skeleton height={300} width='100%' />
  }

  if (isError) {
    return <IconAlert level={AlertLevel.ERROR} title='Error loading sold wool summary' />
  }

  const tableRows = useMemo(() => {
    if (!data) return null

    const rows = [
      {
        label: '',
        getValue: (season: SoldWoolSummary) => season.season,
        key: (i: number) => `${i}-season`,
      },
      {
        label: 'Total Bales',
        getValue: (season: SoldWoolSummary) => (
          <NumberFormatter value={season.totalBales || 0} thousandSeparator />
        ),
        key: (i: number) => `${i}-totalBales`,
      },
      {
        label: 'Total Kilos',
        getValue: (season: SoldWoolSummary) => (
          <NumberFormatter value={season.totalKgs || 0} thousandSeparator />
        ),
        key: (i: number) => `${i}-totalKgs`,
      },
      {
        label: 'Average Bale Weight',
        getValue: (season: SoldWoolSummary) => (
          <NumberFormatter value={season.averageBaleWeight || 0} thousandSeparator />
        ),
        key: (i: number) => `${i}-averageBaleWeight`,
      },
      {
        label: 'Total Gross Proceeds',
        getValue: (season: SoldWoolSummary) => (
          <NumberFormatter
            prefix='$'
            value={season.totalGrossProceedsExGST || 0}
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
          />
        ),
        key: (i: number) => `${i}-totalGrossProceedsExGST`,
      },
      {
        label: 'Average Gross Price per\u00A0Kg',
        getValue: (season: SoldWoolSummary) => (
          <NumberFormatter
            prefix='$'
            value={season.averageGrossPricePerKgExGST || 0}
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
          />
        ),
        key: (i: number) => `${i}-averageGrossPricePerKgExGST`,
      },
      {
        label: 'Total Net Proceeds',
        getValue: (season: SoldWoolSummary) => (
          <NumberFormatter
            prefix='$'
            value={season.totalNetProceedsIncGST || 0}
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
          />
        ),
        key: (i: number) => `${i}-totalNetProceedsIncGST`,
      },
      {
        label: 'Average Net Price per\u00A0Kg',
        getValue: (season: SoldWoolSummary) => (
          <NumberFormatter
            prefix='$'
            value={season.averageNetPricePerKgIncGST || 0}
            thousandSeparator
            fixedDecimalScale
            decimalScale={2}
          />
        ),
        key: (i: number) => `${i}-averageNetPricePerKgIncGST`,
      },
    ]

    return rows.map((row, rowNumber) => (
      <Table.Tr key={row.label || 'header'}>
        <Table.Th>{row.label}</Table.Th>
        {data.map((season, index) =>
          rowNumber === 0 ? (
            <Table.Th key={row.key(index)}>{row.getValue(season)}</Table.Th>
          ) : (
            <Table.Td key={row.key(index)}>{row.getValue(season)}</Table.Td>
          )
        )}
      </Table.Tr>
    ))
  }, [data])

  return (
    <Paper withBorder>
      <Table.ScrollContainer minWidth={800}>
        <Table
          aria-label='Sold Wool Summary'
          variant='vertical'
          withRowBorders
          stickyHeader
          classNames={{
            th: classes.th,
            td: classes.td,
          }}
        >
          <Table.Tbody>{tableRows}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Paper>
  )
}
